import { Menu, MenuOpen } from "@mui/icons-material";
import { AppBar, Box, IconButton, Stack, Toolbar } from "@mui/material";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import useCurrentUser from "~/modules/auth/hooks/useCurrentUser";
import UserBadge from "~/modules/user/components/UserBadge";

import useDesktopLayout from "../../hooks/useDesktopLayout";
import useMobileLayout from "../../hooks/useMobileLayout";
import GaiaLogo from "../GaiaLogo";
import Notification from "../Notification/components/Notification";

export const TOPBAR_HEIGHT = 48;

export default function GaiaTopMenu({ children }: PropsWithChildren) {
    const { isMenuOpen, setIsMenuOpen } = useMobileLayout();
    const { isSidebarCollapsed } = useDesktopLayout();
    const user = useCurrentUser();
    const { t } = useTranslation("app");

    return (
        <AppBar
            position="sticky"
            color="transparent"
            elevation={0}
            sx={theme => ({
                flexShrink: 0,
                backgroundColor: theme.palette.background.default,
            })}
        >
            <Toolbar variant="dense" disableGutters sx={{ minHeight: TOPBAR_HEIGHT, height: TOPBAR_HEIGHT, px: 1 }}>
                <IconButton onClick={() => setIsMenuOpen(true)} size="small" sx={{ display: { sm: "none" }, p: 0.5 }}>
                    {isMenuOpen ? <MenuOpen /> : <Menu />}
                </IconButton>
                <Box
                    component={Link}
                    to="/"
                    aria-label={t("brand.name")}
                    sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
                >
                    <Box sx={{ p: 0.5 }}>
                        <GaiaLogo variant="squareImage" size={32} />
                    </Box>
                    <Box
                        sx={theme => ({
                            overflow: "hidden",
                            // Any value ≥ the text logo's natural width — sizes come from the image.
                            maxWidth: isSidebarCollapsed ? 0 : 300,
                            transition: theme.transitions.create("max-width", { duration: theme.transitions.duration.standard }),
                        })}
                    >
                        <Box
                            sx={theme => ({
                                opacity: isSidebarCollapsed ? 0 : 1,
                                transition: theme.transitions.create("opacity", {
                                    duration: theme.transitions.duration.standard,
                                }),
                            })}
                        >
                            <GaiaLogo variant="textImage" size={18} />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }}>{children}</Box>
                <Stack gap={1} direction="row">
                    <Notification />
                    {user && (
                        <UserBadge
                            profilePicture={user.profilePictureMedia?.url}
                            names={[user.firstName, user.lastName]}
                            size={32}
                            linkTo="/profile"
                        />
                    )}
                </Stack>
            </Toolbar>
        </AppBar>
    );
}
